//@flow
import type { MediaPath, RecommendedScreenType } from 'types/common';
import type { AppState } from 'modules/rootReducer';
import { RestaurantSelector } from 'modules/restaurant/selector';
import { filter, flattenDeep, get, groupBy, isEmpty, values } from 'lodash';
import { DateUtil } from 'utils/DateUtil';
import { CommonMapper } from 'mappers/common';
import MenuItemTemplateImage from 'assets/images/Template-image-Menu-item-v1-01.png';

let locationString = window.location.pathname.toString().split('/');
let outlet_slug = locationString[3];

const mapRestaurantCategories = (
  restaurantCategories: Array<any>,
  mediaPath: MediaPath
): Array<RestaurantCategory> => {
  if (!restaurantCategories || isEmpty(restaurantCategories)) {
    return [];
  }
  const restaurant_details = restaurantCategories.restaurant;
  const outlet_data = restaurantCategories.outlet_data;
  let outlets = filter(
    outlet_data,
    extractedData => extractedData.url_slug === outlet_slug
  );
  return outlets.map(category =>
    mapRestaurantOutletCategory(category, mediaPath, restaurant_details)
  );
};

const mapRestaurantOutletCategory = (
  category: any,
  mediaPath: MediaPath,
  restaurant_details
) => {
  const categoryMenu = category.restaurant_menu_categories || [];
  const category_data = categoryMenu.map(item => {
    return mapRestaurantCategory(item, mediaPath, restaurant_details);
  });
  return category_data;
};

const mapRestaurantCategory = (
  category: any,
  mediaPath: MediaPath,
  restaurant_details
) => {
  const menu = category.restaurant_menu_items.map(item => {
    return mapMenuItems(item, mediaPath);
  });
  const menu_mobile_image_name = restaurant_details.menu_mobile_image;
  return {
    id: category.id,
    name: category.category_name,
    description: category.category_description,
    listImageUrl:
      category && category.category_image === null
        ? MenuItemTemplateImage
        : `${mediaPath.web}/${category.category_image}`,
    listMobileImageUrl: `${mediaPath.mobile}/${menu_mobile_image_name}`,
    mobile_menu_description_first: restaurant_details.menu_description_first,
    mobile_menu_description_second: restaurant_details.menu_description_second,
    status: category.status,
    thumbnailListImageUrl: `${mediaPath.thumbnail}/${category.category_image}`,
    thumbnailListMobileImageUrl: `${mediaPath.thumbnail}/${category.category_image}`,
    menu,
    restaurantOutletID: category.restaurant_outlet_id,
  };
};

const mapMenuItems = (item: any, mediaPath: MediaPath) => {
  return {
    id: item.id,
    name: item.item_name,
    caption: item.item_name,
    description: item.item_description,
    item_mobile_description: item.item_mobile_description,
    price: item.item_price,
    mediumImageUrl: item.item_image
      ? `${mediaPath.web}/${item.item_image}`
      : MenuItemTemplateImage,
    thumbnailListImageUrl: item.item_image
      ? `${mediaPath.thumbnail}/${item.item_image}`
      : MenuItemTemplateImage,
    categoryId: item.restaurant_menu_category_id,
    status: item.status,
    currencyType: item.currency_type,
  };
};

const mapRestaurantsList = (
  restaurantList: Array<any>,
  mediaPath: MediaPath
): Array<RecommendedScreenType> => {
  if (!restaurantList || restaurantList.length === 0) {
    return [];
  }
  const finalList = restaurantList.map(restaurant =>
    mapRestaurant(restaurant, mediaPath)
  );
  return flattenDeep(finalList);
};

// TODO check how to flat an obj
const mapRestaurant = (restaurant: any, mediaPath: MediaPath) => {
  const restaurantDetail = restaurant.restaurant_details || [];
  const restaurantOutlet = restaurant.restaurant_outlets || [];
  const restaurantBasicDetails = {
    information: restaurantDetail[0].restaurant_description,
    urlSlug: restaurant.url_slug,
  };
  if (!restaurantOutlet || restaurantOutlet.length === 0) {
    return [];
  }
  return restaurantOutlet.map(restaurant_outlets =>
    mapRestaurantOutlet(restaurant_outlets, restaurantBasicDetails, mediaPath)
  );
};

const mapRestaurantOutlet = (
  restaurantOutlet: any,
  restaurantBasicDetails: any,
  mediaPath: MediaPath
) => {
  const area = get(restaurantOutlet, 'area.area_name', '');
  const place = get(restaurantOutlet, 'city.city_name', '');
  const location = `${area}, ${place}`;

  const isRestaurantOpen = DateUtil.getClosedOpenStatus(
    restaurantOutlet.opens_at_morning,
    restaurantOutlet.closed_at
  );
  const isTodayClosed =
    restaurantOutlet.is_today_close === 'Yes'
      ? true
      : DateUtil.getIsTodayRestaurantClose(restaurantOutlet.closing_day);
  return {
    id: restaurantOutlet.id,
    location: restaurantOutlet.address,
    urlSlug: restaurantBasicDetails.urlSlug,
    outletUrlSlug: restaurantOutlet.url_slug,
    image: `${mediaPath.web}/${restaurantOutlet.restaurant_detail_image}`,
    listImageUrl: `${mediaPath.web}/${restaurantOutlet.restaurant_list_image}`,
    listMobileImageUrl: restaurantOutlet.restaurant_mobile_list_image
      ? `${mediaPath.mobile}/${restaurantOutlet.restaurant_mobile_list_image}`
      : `${mediaPath.web}/${restaurantOutlet.restaurant_list_image}`,
    thumbNailUrl: `${mediaPath.thumbnail}/${restaurantOutlet.restaurant_detail_image}`,
    information: restaurantBasicDetails.information,
    name: restaurantOutlet.outlet_name,
    city: location,
    phoneNo: restaurantOutlet.restaurant_phone,
    cityId: restaurantOutlet.city.id,
    cityName: restaurantOutlet.city.city_name,
    opens_at: restaurantOutlet.opens_at,
    address: restaurantOutlet.address,
    timings: isRestaurantOpen
      ? `CLOSES AT ${restaurantOutlet.closed_at}`
      : `OPENS AT ${restaurantOutlet.opens_at_morning}`,
    currentStatus: isRestaurantOpen ? 'OPEN NOW' : 'CLOSED NOW',
    isTodayClose: isTodayClosed,
    facebookLink: restaurantOutlet.facebook_link,
    instagramLink: restaurantOutlet.instagram_link,
    twitterLink: restaurantOutlet.twitter_link,
    visitOurStoreLink: restaurantOutlet.vist_our_store_link,
    merchandiseLink: restaurantOutlet.merchandise_link,
    orderOnlineLink: restaurantOutlet.order_online_link,
    reservationLink: restaurantOutlet.reservation_link,
    exploreLink: restaurantOutlet.external_explore_link,
  };
};

const mapRestaurantDetailsBanner = (
  restaurantDetails: any,
  mediaPath: MediaPath
) => {
  let restaurant_url_slug = restaurantDetails.url_slug;
  let restaurantBasicDetails = restaurantDetails.restaurant_details[0];
  let outlets = filter(
    restaurantDetails.restaurant_outlets,
    extractedData => extractedData.url_slug === outlet_slug
  );
  return outlets.map(outlet =>
    mapRestaurantOutletBanner(
      outlet,
      restaurantBasicDetails,
      restaurant_url_slug,
      mediaPath
    )
  );
};

const mapRestaurantOutletBanner = (
  restaurantOutlet: any,
  restaurantBasicDetails: any,
  restaurant_url_slug: string,
  mediaPath: MediaPath
) => {
  const isRestaurantOpen = DateUtil.getClosedOpenStatus(
    restaurantOutlet.opens_at_morning,
    restaurantOutlet.closed_at
  );

  const isTodayClosed =
    restaurantOutlet.is_today_close === 'Yes'
      ? true
      : DateUtil.getIsTodayRestaurantClose(restaurantOutlet.closing_day);

  const fetchBannerImageURl = (image, type) => {
    if (type === 'web') return `${mediaPath.web}/${image}`;
    if (type === 'mobile') return `${mediaPath.mobile}/${image}`;
    if (type === 'thumbnail') return `${mediaPath.thumbnail}/${image}`;
  };

  const bannerCarousalImages = () => {
    let banner_carousal_images = [];
    let mobileData = [];
    if (restaurantOutlet.restaurant_detail_image) {
      let banner_image_obj = {};
      banner_image_obj.image = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image,
        'web'
      );
      banner_image_obj.mobile_image = fetchBannerImageURl(
        restaurantOutlet.restaurant_mobile_detail_image
          ? restaurantOutlet.restaurant_mobile_detail_image
          : restaurantOutlet.restaurant_mobile_list_image
          ? restaurantOutlet.restaurant_mobile_list_image
          : restaurantOutlet.restaurant_detail_image,
        'mobile'
      );
      banner_image_obj.title = restaurantOutlet.banner_title1
        ? restaurantOutlet.banner_title1
        : restaurantBasicDetails.restaurant_name;
      banner_image_obj.thumbnail = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image,
        'thumbnail'
      );
      banner_carousal_images.push(banner_image_obj);
    }
    if (restaurantOutlet.restaurant_detail_image1) {
      let banner_image_obj = {};
      banner_image_obj.image = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image1,
        'web'
      );
      banner_image_obj.mobile_image = restaurantOutlet.restaurant_mobile_detail_image1
        ? fetchBannerImageURl(
            restaurantOutlet.restaurant_mobile_detail_image1,
            'mobile'
          )
        : '';
      banner_image_obj.title = restaurantOutlet.banner_title2
        ? restaurantOutlet.banner_title2
        : restaurantBasicDetails.restaurant_name;
      banner_image_obj.thumbnail = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image1,
        'thumbnail'
      );
      banner_carousal_images.push(banner_image_obj);
    }
    if (restaurantOutlet.restaurant_detail_image2) {
      let banner_image_obj = {};
      banner_image_obj.image = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image2,
        'web'
      );
      banner_image_obj.mobile_image = restaurantOutlet.restaurant_mobile_detail_image2
        ? fetchBannerImageURl(
            restaurantOutlet.restaurant_mobile_detail_image2,
            'mobile'
          )
        : '';
      banner_image_obj.title = restaurantOutlet.banner_title3
        ? restaurantOutlet.banner_title3
        : restaurantBasicDetails.restaurant_name;
      banner_image_obj.thumbnail = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image2,
        'thumbnail'
      );
      banner_carousal_images.push(banner_image_obj);
    }
    if (restaurantOutlet.restaurant_detail_image3) {
      let banner_image_obj = {};
      banner_image_obj.image = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image3,
        'web'
      );
      banner_image_obj.mobile_image = restaurantOutlet.restaurant_mobile_detail_image3
        ? fetchBannerImageURl(
            restaurantOutlet.restaurant_mobile_detail_image3,
            'mobile'
          )
        : '';
      banner_image_obj.title = restaurantOutlet.banner_title4
        ? restaurantOutlet.banner_title4
        : restaurantBasicDetails.restaurant_name;
      banner_image_obj.thumbnail = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image3,
        'thumbnail'
      );
      banner_carousal_images.push(banner_image_obj);
    }
    if (restaurantOutlet.restaurant_detail_image4) {
      let banner_image_obj = {};
      banner_image_obj.image = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image4,
        'web'
      );
      banner_image_obj.mobile_image = restaurantOutlet.restaurant_mobile_detail_image4
        ? fetchBannerImageURl(
            restaurantOutlet.restaurant_mobile_detail_image4,
            'mobile'
          )
        : '';
      banner_image_obj.title = restaurantOutlet.banner_title5
        ? restaurantOutlet.banner_title5
        : restaurantBasicDetails.restaurant_name;
      banner_image_obj.thumbnail = fetchBannerImageURl(
        restaurantOutlet.restaurant_detail_image4,
        'thumbnail'
      );
      banner_carousal_images.push(banner_image_obj);
    }
    banner_carousal_images.forEach((data, idx) => {
      if (data.mobile_image !== '') {
        mobileData.push(data);
      }
    });
    return { banner_carousal_images, mobileData };
  };

  return {
    id: restaurantBasicDetails.id,
    outlet_id: restaurantOutlet.id,
    name: restaurantBasicDetails.restaurant_name,
    desktop_carousal_data: bannerCarousalImages().banner_carousal_images,
    carousal_mobile_data: bannerCarousalImages().mobileData,
    outlet_name: restaurantOutlet.outlet_name,
    description: restaurantBasicDetails.restaurant_description,
    country: restaurantOutlet.country
      ? restaurantOutlet.country.country_name
      : '',
    city: restaurantOutlet.city.city_name,
    area: restaurantOutlet.area.area_name,
    phone: restaurantOutlet.restaurant_phone,
    address: restaurantOutlet.address,
    morningOpeningTime: restaurantOutlet.opens_at_morning,
    morningClosingTime: restaurantOutlet.closes_at_morning,
    eveningOpeningTime: restaurantOutlet.opens_at,
    eveningClosingTime: restaurantOutlet.closed_at,
    isClosed: restaurantOutlet.is_today_close,
    closingDate: restaurantOutlet.closing_day,
    closingReason: restaurantOutlet.close_reason,
    listImageUrl: restaurantOutlet.restaurant_list_image,
    detailImageUrl: `${mediaPath.web}/${restaurantOutlet.restaurant_detail_image}`,
    thumbnail: `${mediaPath.thumbnail}/${restaurantOutlet.restaurant_detail_image}`,
    outletSlug: restaurantOutlet.url_slug,
    urlSlug: restaurant_url_slug,
    restaurant_menu_categories: restaurantOutlet.restaurant_menu_categories,
    // //TODO: refactor
    isTodayClose: isTodayClosed,
    opens_at: restaurantOutlet.opens_at_morning,
    timings: isRestaurantOpen
      ? `CLOSES AT ${restaurantOutlet.closed_at}`
      : `OPENS AT ${restaurantOutlet.opens_at_morning}`,
    currentStatus: isRestaurantOpen ? 'OPEN NOW' : 'CLOSED NOW',
    facebookLink: restaurantOutlet.facebook_link,
    instagramLink: restaurantOutlet.instagram_link,
    twitterLink: restaurantOutlet.twitter_link,
    visitOurStoreLink: restaurantOutlet.vist_our_store_link,
    merchandiseLink: restaurantOutlet.merchandise_link,
    orderOnlineLink: restaurantOutlet.order_online_link,
    reservationLink: restaurantOutlet.reservation_link,
    reservationFormDescription: restaurantOutlet.reservation_form_description,
    reservationOutletId: restaurantOutlet.reservation_outlet_id,
    email: restaurantOutlet.email,
    title: 'Reserve your unique dining experience',
    metaTagSEO: {
      metaTitle: restaurantOutlet.meta_title,
      metaKeywords: restaurantOutlet.meta_keyword,
      metaDescription: restaurantOutlet.meta_description,
    },
  };
};

const mapSisterRestaurantData = (
  state: AppState,
  defaultOutletSlug: string
) => {
  const allRestaurants = RestaurantSelector.getAllRestaurants(state);

  if (allRestaurants && allRestaurants.length) {
    return filter(allRestaurants, function(n) {
      return defaultOutletSlug !== n.outletUrlSlug;
    });
  }
  return [];
};

const mapRestaurantGalleryData = (galleryImages, mediaPath) => {
  const outlet_gallery = CommonMapper.mapGalleryImages(
    galleryImages,
    mediaPath
  );
  if (outlet_gallery) {
    return outlet_gallery;
  }
  return [];
};

const mapBeerDetails = (response: any, mediaPath: MediaPath) => {
  let beers = [];
  if (response.length > 0) {
    response.forEach((beer, idx) => {
      const beerData = {
        name: beer.beer_name,
        restaurantName: beer.restaurant_outlet.outlet_name,
        description: beer.beer_description,
        descriptionMobile: beer.beer_mobile_description,
        image: `${mediaPath.web}/${beer.picture}`,
        imageMobile: `${mediaPath.mobile}/${beer.mobile_picture}`,
      };
      beers.push(beerData);
    });
    return beers;
  }
  return [];
};

export const RestaurantMapper = {
  mapRestaurantCategories,
  mapRestaurantsList,
  mapRestaurantDetailsBanner,
  mapSisterRestaurantData,
  mapRestaurantGalleryData,
  mapBeerDetails,
};
